
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DatePickerInput } from '../ui/date';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel
} from "@/components/ui/form"
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { Dropdown } from '../ui/filterSelect';
import { NumberInput } from '../ui/number';

import { OrderSchema } from '../order/OrderSchema';

import { Trash2 } from "lucide-react";
import { useApiFetch } from "@/helper/apiFetch";
import { useTranslate } from "@/helper/translate";
type OrderInfoProps = {
  Id?: string;
  CustomerId?: string;
  SiteId?: string;
  onClose: (order: string|null) => void;
};



type Lookup = { id: string; name: string };
type ContainerLookup = { siteId: string; container: Lookup[] };


const rotations = [
  { id: 1, name: "L" },
  { id: 2, name: "M" },
  { id: 3, name: "S" }
];
const lines = [
  { id: 0, name: "1 & 2" },
  { id: 1, name: "1" },
  { id: 2, name: "2" }
];


export function OrderInfo({ Id, CustomerId, SiteId, onClose }: OrderInfoProps) {
  const apiFetch = useApiFetch();
  const { translate } = useTranslate();
  //coded for admin only, no customer should be in this screen changing orders
  const [isLoaded, setIsLoaded] = useState(false);
  const [customers, setCustomers] = useState<Lookup[]>([]);
  const [containers, setContainers] = useState<ContainerLookup[]>([]);
  const [sites, setSites] = useState<Lookup[]>([]);
  const [errorText, setError] = useState("");


  const form = useForm<z.infer<typeof OrderSchema>>({
    resolver: zodResolver(OrderSchema),
    defaultValues: {
      orderName: undefined,
      siteId: SiteId,
      customerId: CustomerId,
      orderContainerId: undefined,
      rotation: 1,
      orderBegin: undefined,
      orderEnd: undefined,
      orderLength: undefined

    }
  });




  const onSubmit = async (values: z.infer<typeof OrderSchema>) => {
    try {
      setError("");

      
      const res = await apiFetch(`/App/SaveOrder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!res.ok) {
        const e = await res.text();
        setError(translate("ui.failedsave") + e);

      }
      else {

        onClose( JSON.stringify(values));

      }
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {

    apiFetch(`/App/GetCustomerLookup?all=true`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch customers");
        return res.json();
      })
      .then((data) => {
        setCustomers(data);

      })
      .catch((err) => {
        console.error(err);
      });


  }, []);

  useEffect(() => {
    if (customers.length > 0) {
      apiFetch(`/App/GetSiteLookup`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch sites");
          return res.json();
        })
        .then((data) => {
          setSites(data);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [customers]);


  useEffect(() => {
    if (sites.length > 0) {
      if (Id && Id?.length > 0) {
        apiFetch(`/App/GetOrder?id=${Id}`)
          .then((res) => {
            if (!res.ok) throw new Error("Failed to fetch order");
            return res.json();
          })
          .then((orderData) => {
            orderData.orderSites = [];
            let site = { siteId: orderData.siteId, orderContainerId: orderData.orderContainerId , lineId: orderData.lineId};
            orderData.orderSites.push(site);

            form.reset(orderData);
            setIsLoaded(true);
          })
          .catch((err) => {
            console.error(err);
          });
      }
      else {
        //set default array
        const sites = Array.from({ length: 1 }, () => ({ siteId: SiteId ?? null, orderContainerId: "", lineId:null }));
        form.setValue("orderSites", sites, { shouldDirty: true });
        setIsLoaded(true);
      }
    }

  }, [sites]);




  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "orderSites",
  });
  //to load containers when site changes
  const siteValues = useWatch({ control: form.control, name: "orderSites" }) ?? [];

  useEffect(() => {

    if (!siteValues.length) return;
    siteValues.forEach(site => {


      if (!site) return; // guard if schema allows null
      if (!site.siteId) return; // guard if schema allows null

      // skip if we already have it
      if (containers.some(c => c.siteId === site.siteId)) return;

      apiFetch(`/App/GetContainerLookup?id=${site.siteId}`)
        .then(res => {
          if (!res.ok) throw new Error("Failed to fetch containers");
          return res.json();
        })
        .then(data => {
          // append immutably
          setContainers(prev => [
            ...prev,
            { siteId: site.siteId, container: data } as ContainerLookup,
          ]);
        })
        .catch(console.error);
    });
  }, [siteValues]);

  return (
    <div className="orderInfo p-0">
      {!isLoaded ? (
        <div className="w-full text-center my-10">{translate("common.loading")}</div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, (error) => { console.log("Validation errors:", error, form.getValues()); })} className="space-y-2">

          <div className="bg-white dark:bg-zinc-600 rounded-2xl shadow-lg space-y-5 mx-[-10px] my-1 p-3">

            <div className="grid-default">

              <FormField
                control={form.control}
                name="customerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{translate("ui.company")}</FormLabel>
                    <FormControl>
                      <Dropdown
                        {...field}
                        source={customers}
                        getLabel={(x) => x.name}
                        getValue={(x) => x.id}
                        searchable={true}
                        disabled={Id != null && Id != ""}

                      />
                    </FormControl>
                  </FormItem>
                )}
              />


              <FormField
                control={form.control}
                name="orderName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{translate("ui.name")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="grid-default">
                <FormField
                  control={form.control}
                  name="orderBegin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{translate("ui.startdate")}</FormLabel>
                      <FormControl>
                        <DatePickerInput
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="orderEnd"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{translate("ui.enddate")}</FormLabel>
                      <FormControl>
                        <DatePickerInput
                          {...field}
                          setMidnight={true}

                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid-default">


                <FormField
                  control={form.control}
                  name="rotation"
                  render={({ field }) => (
                    <FormItem className="col-span-1">
                      <FormLabel>{translate("ui.rotation")}</FormLabel>
                      <FormControl>
                        <Dropdown
                          {...field}
                          source={rotations}
                          getLabel={(x) => x.name}
                          getValue={(x) => x.id}

                        />
                      </FormControl>
                    </FormItem>
                  )}
                />



                <FormField
                  control={form.control}
                  name="orderLength"

                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{translate("ui.orderLength")}</FormLabel>
                      <FormControl>
                        <NumberInput
                          value={field.value ?? ""}
                          onChange={field.onChange}

                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            {fields?.map((site, idx) => {
              const siteId = siteValues[idx]?.siteId ?? null;
              
              return (
                <div className="flex gap-2 items-center" key={site.id}>
                  <FormField
                    control={form.control}
                    name={`orderSites.${idx}.siteId`}
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>{translate("ui.site")}</FormLabel>
                        <FormControl>
                          <Dropdown

                            {...field}
                            source={sites}
                            getLabel={(x) => x.name}
                            getValue={(x) => x.id}
                            value={field.value ?? ""}                   // id string or ""
                            onChange={(id) => { field.onChange(id || null); console.log(site) }}
                            disabled={Id != null && Id != ""}

                          />
                        </FormControl>
                      </FormItem>

                    )}
                  />
                  {siteId?.toUpperCase() == "E8C4F476-342A-4143-ACF0-888FC9767473" &&
                    (
                      
                        <FormField
                          control={form.control}
                          name={`orderSites.${idx}.lineId`}
                          render={({ field }) => (
                            <FormItem className="min-w-[100px] max-w-[100px]">
                              <FormLabel>{translate("ui.line")}</FormLabel>
                              <FormControl>
                                <Dropdown
                                   
                                  {...field}
                                  source={lines}
                                  getLabel={(x) => x.name}
                                  getValue={(x) => x.id}
                                  searchable={true}
                                  disabled={Id != null && Id != ""}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        
                    )}
                  
                  {siteId != null && containers.some(c => c.siteId == siteId) &&
                    (
                     

                        
                        <FormField
                          control={form.control}
                          name={`orderSites.${idx}.orderContainerId`}
                          render={({ field }) => (
                            <FormItem className="w-full">
                              <FormLabel>{translate("ui.container")}</FormLabel>
                              <FormControl>
                                <Dropdown
                                  {...field}
                                  source={containers.find(c => c.siteId == siteId)?.container as Lookup[]}
                                  getLabel={(x) => x.name}
                                  getValue={(x) => x.id}
                                  searchable={true}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        
                     
                    )}
                    {idx > 0 && (
                          <div className="pt-7 pl-4">
                            <Trash2 className="cursor-pointer bg-rose-600 hover:bg-rose-700 rounded-full text-white p-[3px]" type="button" onClick={() => remove(idx)} />
                          </div>

                        )}
                </div>)
            })}


            {Id == null || Id == "" && (

              <div className="grid gap-2 grid-cols-4">
                <Button className="w-full" type="button" onClick={() => { append({ siteId: null, orderContainerId: null }) }}>{translate("ui.addSite")}</Button>
              </div>
            )}
          </div>

            <div className="grid-default">
              <div></div>
              <div className="grid-default">
                <Button className="w-full" type="button" onClick={() => { console.log("Cancel clicked"); onClose(null); }}>{translate("common.cancel")}</Button>

                <Button className="w-full" type="submit">{translate("common.save")}</Button>
              </div>
            </div>
            {(errorText != "") && (
              <div className="text-red-500 mt-2">
                {errorText}
              </div>
            )
            }
          </form>
        </Form>)}
    </div>

  )
}



